import { showSuccess, showError, showHttpError } from '@calendly/alert'
import { I18n } from '@calendly/i18n'
import { capitalize, cloneDeep } from 'lodash-es'
import { nanoid } from 'nanoid'

import { TEMPLATE_TYPE } from 'features/tracked_meetings'
import { rootApi } from 'root-api'
import { rootApi as graphqlApi } from 'root-api-graphql'
import { camelize } from 'services/string_utils'

import { sampleSummary, sampleTrackedMeeting } from '../utils'

const NOTETAKER_TRACKED_MEETINGS = '/api/notetaker/tracked_meetings'
const NOTETAKER_POST_CONTENT_EDIT = '/api/notetaker/feedback_content_edit'
const NOTETAKER_POST_SET_EVENT_TEMPLATE = '/api/notetaker/set_event_template'

const FETCH_TRACKED_MEETING_WITH_SUMMARIES_BY_FOREIGN_ID = 'fetchTrackedMeetingWithSummariesByForeignId'
const FETCH_TRACKED_MEETING_BY_FOREIGN_ID = 'fetchTrackedMeetingByForeignId'


const trackedMeetingPath = (meetingUuid) => `${NOTETAKER_TRACKED_MEETINGS}/${meetingUuid}`

const trackedMeetingSummaryPath = (meetingUuid, template) => {
  const base = `${trackedMeetingPath(meetingUuid)}/summary`
  if (!template || template === TEMPLATE_TYPE.AUTO) return base
  return `${base}?template=${template}`
}

const unwrapNotetakerResource = (response) => response?.resource ?? response

const splitByLinesAndAddKeys = (document) => (
  document?.split && document.split('\n').map((line) => (
    { text: `${line.trim()}\n`, key: nanoid(8) }
  ))
)

export const transformSummaries = (summaries) => Object.fromEntries(Object.entries(summaries).map((summary) => {
  const [summaryName, rawValue] = summary

  // Special case for transforming summaries dict
  if (summaryName === 'summaries') {
    rawValue.forEach((item) => {
      item.value = splitByLinesAndAddKeys(item.value)
    })
  }

  if (typeof rawValue !== 'string' || summaryName === 'template' || summaryName === 'templateStatus') {
    return [summaryName, rawValue]
  }

  const parsedText = splitByLinesAndAddKeys(rawValue)
  return [summaryName, parsedText]
}))

const api = rootApi.injectEndpoints({
  endpoints: (build) => ({
    tagTypes: ['RecapStatuses', 'TrackedMeetings', 'TrackedMeetingWithSummary'],
    destroyTrackedMeeting: build.mutation({
      query: ({ meetingUuid }) => ({
        url: `/api/tracked_meetings/${meetingUuid}`,
        method: 'DELETE'
      }),
      onQueryStarted: async ({ foreignEventId, skipInvalidation }, lifecycleAPI) => {
        const optimisticUpdateWithSummaries = lifecycleAPI.dispatch(
          api.util.updateQueryData(FETCH_TRACKED_MEETING_WITH_SUMMARIES_BY_FOREIGN_ID, foreignEventId, (draft) => {
            draft.deleted = true
          })
        )
        const optimisticUpdate = lifecycleAPI.dispatch(
          api.util.updateQueryData(FETCH_TRACKED_MEETING_BY_FOREIGN_ID, foreignEventId, (draft) => {
            draft.deleted = true
          })
        )

        try {
          await lifecycleAPI.queryFulfilled
          if (!skipInvalidation) {
            const { dispatch } = lifecycleAPI
            dispatch(
              api.util.invalidateTags([
                'RecapStatuses',
                'TrackedMeetings',
                'Meeting',
                { type: 'TrackedMeetingWithSummary', id: foreignEventId },
                { type: 'TrackedMeeting', id: foreignEventId }
              ])
            )

            dispatch(graphqlApi.util.invalidateTags(['MeetingRecaps', 'MeetingSearchResult']))
          }
        } catch (error) {
          optimisticUpdateWithSummaries.undo()
          optimisticUpdate.undo()
          console.error('[DELETE MUTATION] Error during invalidation:', error)
        }
      },
    }),
    removeTrackedMeetingBot: build.mutation({
      query: ({ meetingUuid }) => ({
        url: `/api/tracked_meetings/${meetingUuid}/remove_bot`,
        method: 'POST'
      }),
      onQueryStarted: async ({ foreignEventId }, { dispatch, queryFulfilled }) => {
        const optimisticUpdateWithSummaries = dispatch(
          api.util.updateQueryData(FETCH_TRACKED_MEETING_WITH_SUMMARIES_BY_FOREIGN_ID, foreignEventId, (draft) => {
            // Important: has to be PascalCase to match what is returned from the API
            draft.status = 'MeetingEnded'
          })
        )
        const optimisticUpdate = dispatch(
          api.util.updateQueryData(FETCH_TRACKED_MEETING_BY_FOREIGN_ID, foreignEventId, (draft) => {
            // Important: has to be PascalCase to match what is returned from the API
            draft.status = 'MeetingEnded'
          })
        )

        try {
          const { data } = await queryFulfilled
          dispatch(
            api.util.invalidateTags([
              'Meeting',
              'RecapStatuses',
              'TrackedMeetings',
              { type: 'TrackedMeetingWithSummary', id: foreignEventId },
              { type: 'TrackedMeeting', id: foreignEventId }
            ])
          )
          if (data.message?.includes('Notetaker already removed')) {
            dispatch(showSuccess(
              I18n.t('publisher.meeting_intelligence.notetaker_toggle.notetaker_already_removed')
            ))
          }
        } catch {
          optimisticUpdateWithSummaries.undo()
          optimisticUpdate.undo()
        }
      }
    }),
    addTrackedMeeting: build.mutation({
      query: ({ eventId }) => ({
        url: `/api/dashboard/events/${eventId}/add_ai_notetaker`,
        method: 'POST'
      }),
      invalidatesTags: (_, error, { eventUuid }) => {
        const type = error?.type
        if (type === 'conflict') return []

        return [
          'RecapStatuses',
          'TrackedMeetings',
          'Meeting',
          { type: 'TrackedMeeting', id: eventUuid },
          { type: 'TrackedMeetingWithSummary', id: eventUuid }
        ]
      },
      onQueryStarted: async ({ eventUuid }, { dispatch, queryFulfilled }) => {
        const optimisticUpdateWithSummaries = dispatch(
          api.util.updateQueryData(FETCH_TRACKED_MEETING_WITH_SUMMARIES_BY_FOREIGN_ID, eventUuid, (draft) => {
            draft.enabled = true
            draft.status = 'Scheduled'
          })
        )
        const optimisticUpdate = dispatch(
          api.util.updateQueryData(FETCH_TRACKED_MEETING_BY_FOREIGN_ID, eventUuid, (draft) => {
            draft.enabled = true
            draft.status = 'Scheduled'
          })
        )

        try {
          await queryFulfilled
        } catch (e) {
          optimisticUpdateWithSummaries.undo()
          optimisticUpdate.undo()
          const type = e?.error?.type

          if (type === 'conflict') {
            dispatch(showError(
              I18n.t('publisher.meeting_intelligence.notetaker_toggle.notetaker_unable_to_rejoin')
            ))
          } else {
            dispatch(showHttpError(e.error))
          }
        }
      },
      extraOptions: {
        handleErrors: false,
      }
    }),
    addTrackedExternalMeeting: build.mutation({
      query: ({ eventUuid, event }) => ({
        url: `/api/dashboard/events/${eventUuid}/add_external_ai_notetaker`,
        method: 'POST',
        data: {
          location: event.location,
          start_time: event.startTime,
          end_time: event.endTime,
          uuid: event.uuid,
          name: event.name,
          source: capitalize(event.source),
          attendees: (event.attendees || []).map((attendee) => ({
            kind: attendee.kind,
            name: attendee.name,
            email: attendee.email,
            timezone: attendee.timezone
          }))
        }
      }),
      invalidatesTags: (_, error, { eventUuid }) => {
        const type = error?.type
        if (type === 'conflict') return []

        return [
          'RecapStatuses',
          'TrackedMeetings',
          'Meeting',
          { type: 'TrackedMeeting', id: eventUuid },
          { type: 'TrackedMeetingWithSummary', id: eventUuid }
        ]
      },
      onQueryStarted: async ({ eventUuid }, { dispatch, queryFulfilled }) => {
        const optimisticUpdateWithSummaries = dispatch(
          api.util.updateQueryData(FETCH_TRACKED_MEETING_WITH_SUMMARIES_BY_FOREIGN_ID, eventUuid, (draft) => {
            draft.enabled = true
            draft.status = 'Scheduled'
          })
        )
        const optimisticUpdate = dispatch(
          api.util.updateQueryData(FETCH_TRACKED_MEETING_BY_FOREIGN_ID, eventUuid, (draft) => {
            draft.enabled = true
            draft.status = 'Scheduled'
          })
        )

        try {
          await queryFulfilled
        } catch (e) {
          optimisticUpdateWithSummaries.undo()
          optimisticUpdate.undo()
          const type = e?.error?.type

          if (type === 'conflict') {
            dispatch(showError(
              I18n.t('publisher.meeting_intelligence.notetaker_toggle.notetaker_unable_to_rejoin')
            ))
          } else {
            dispatch(showHttpError(e.error))
          }
        }
      },
      extraOptions: {
        handleErrors: false,
      }
    }),
    fetchTrackedMeetings: build.query({
      query: (params = {}) => ({
        url: '/api/tracked_meetings',
        params
      }),
      providesTags: ['TrackedMeetings'],
    }),
    fetchRecapStatuses: build.query({
      query: (params = {}) => ({
        url: '/api/notetaker/recap_status',
        params
      }),
      providesTags: ['RecapStatuses'],
    }),
    [FETCH_TRACKED_MEETING_BY_FOREIGN_ID]: build.query({
      async queryFn(foreignEventId, _queryApi, _extraOptions, fetchWithBQ) {
        const trackedMeetingResult = await fetchWithBQ({
          url: '/api/tracked_meetings',
          params: { foreign_event_id: foreignEventId }
        })

        if (trackedMeetingResult.error) {
          return {
            data: {
              enabled: false,
              trackedDataError: true,
              summaryError: true
            }
          }
        }

        const trackedMeeting = trackedMeetingResult.data.events?.[0]

        if (!trackedMeeting) {
          return {
            data: {
              enabled: false,
              trackedDataError: false,
              summaryError: false
            }
          }
        }

        return {
          data: {
            status: trackedMeeting.status,
            trackedMeetingUuid: trackedMeeting.uuid,
            thumbnail: trackedMeeting.thumbnail,
            enabled: true,
            summaryError: false,
            trackedDataError: false,
            durationSeconds: trackedMeeting.artifacts?.videos?.length
              ? trackedMeeting.artifacts.videos[0].attributes.durationSeconds
              : 0,
            // Only include error for non-completed meetings to match old endpoint behavior
            ...(trackedMeeting.status !== 'Completed' && { error: trackedMeeting.error })
          }
        }
      },
      providesTags: (_, __, foreignEventId) => [{ type: 'TrackedMeeting', id: foreignEventId }],
      extraOptions: { handleErrors: false },
    }),
    [FETCH_TRACKED_MEETING_WITH_SUMMARIES_BY_FOREIGN_ID]: build.query({
      async queryFn(foreignEventId, _queryApi, _extraOptions, fetchWithBQ) {
        const trackedMeetingResult = await fetchWithBQ({
          url: '/api/tracked_meetings',
          params: { foreign_event_id: foreignEventId }
        })

        if (trackedMeetingResult.error) {
          return {
            data: {
              enabled: false,
              trackedDataError: true,
              summaryError: true
            }
          }
        }

        const trackedMeeting = trackedMeetingResult.data.events?.[0]

        if (!trackedMeeting) {
          return {
            data: {
              enabled: false,
              trackedDataError: false,
              summaryError: false
            }
          }
        }

        if (trackedMeeting.status !== 'Completed') {
          return {
            data: {
              status: trackedMeeting.status,
              trackedMeetingUuid: trackedMeeting.uuid,
              thumbnail: trackedMeeting.thumbnail,
              enabled: true,
              summaryError: false,
              trackedDataError: false,
              error: trackedMeeting.error
            }
          }
        }

        const meetingSummaryResult = await fetchWithBQ({
          url: trackedMeetingSummaryPath(trackedMeeting.uuid),
        })

        if (meetingSummaryResult.error) {
          return {
            data: {
              status: trackedMeeting.status,
              trackedMeetingUuid: trackedMeeting.uuid,
              thumbnail: trackedMeeting.thumbnail,
              enabled: true,
              summaryError: true,
              trackedDataError: false
            }
          }
        }

        return {
          data: {
            status: trackedMeeting.status,
            summary: unwrapNotetakerResource(meetingSummaryResult?.data)?.summary,
            trackedMeetingUuid: trackedMeeting.uuid,
            thumbnail: trackedMeeting.thumbnail,
            enabled: true,
            summaryError: false,
            trackedDataError: false,
            durationSeconds: trackedMeeting.artifacts?.videos?.length
              ? trackedMeeting.artifacts.videos[0].attributes.durationSeconds
              : 0
          }
        }
      },
      providesTags: (_, __, foreignEventId) => [{ type: 'TrackedMeetingWithSummary', id: foreignEventId }],
      extraOptions: { handleErrors: false },
    }),
    fetchTrackedMeetingWithSummary: build.query({
      async queryFn({ meetingUuid, isSample = false },
        _queryApi, _extraOptions, fetchWithBQ) {
        if (isSample) {
          return {
            data: {
              ...camelize(cloneDeep(sampleTrackedMeeting)).data,
              summaries: transformSummaries(camelize(cloneDeep(sampleSummary)).data),
            },
          }
        }

        const trackedMeetingResult = await fetchWithBQ({
          url: trackedMeetingPath(meetingUuid),
          params: { get_signed_urls: true },
        })
        if (trackedMeetingResult.error) {
          return { error: trackedMeetingResult.error }
        }

        const meetingSummaryResult = await fetchWithBQ({
          url: trackedMeetingSummaryPath(meetingUuid),
        })
        if (meetingSummaryResult.error) {
          return { error: meetingSummaryResult.error }
        }

        return {
          data: {
            ...unwrapNotetakerResource(trackedMeetingResult.data),
            summaries: transformSummaries(unwrapNotetakerResource(meetingSummaryResult.data)),
          },
        }
      },
      extraOptions: {
        handleErrors: false
      }
    }),
    fetchTemplateSummary: build.query({
      query: ({ meetingUuid, template }) => ({
        url: trackedMeetingSummaryPath(
          meetingUuid,
          template === TEMPLATE_TYPE.AUTO ? undefined : template
        ),
      }),
      transformResponse: (response) => transformSummaries(unwrapNotetakerResource(response)),
      async onQueryStarted(params, { queryFulfilled, dispatch }) {
        // Fetching the summary without including a template param with get an auto-detected template.
        // Add the response to the cache for the auto-detected template type.
        if (params.template === TEMPLATE_TYPE.AUTO) {
          const { data: templateSummaryData } = await queryFulfilled

          if (templateSummaryData?.template) {
            dispatch(
              api.util.upsertQueryData(
                'fetchTemplateSummary',
                { meetingUuid: params.meetingUuid, template: templateSummaryData.template },
                templateSummaryData
              )
            )
          }
        }
      }
    }),
    postTrackedMeetingThumbsFeedback: build.mutation({
      query: ({ targetUuid, thumb, thumbSurveySelection = null, thumbSurveyFreeText = null }) => ({
        url: '/api/feedback/thumb',
        method: 'POST',
        params: {
          targetUuid,
          thumb,
          thumbSurveySelection,
          thumbSurveyFreeText
        }
      }),
      onQueryStarted: async ({ targetUuid, thumb, meetingUuid, template }, { dispatch, queryFulfilled }) => {
        const optimisticUpdate = dispatch(
          api.util.updateQueryData('fetchTemplateSummary', { meetingUuid, template }, (draft) => {
            const target = draft.summaries.find((item) => item.targetUuid === targetUuid)
            if (target && 'targetFeedbackThumb' in target) {
              target.targetFeedbackThumb = thumb
            }
          })
        )

        try {
          await queryFulfilled
        } catch {
          optimisticUpdate.undo()
        }
      }
    }),
    fetchThumbFeedbackSurveyOptions: build.query({
      query: (params = {}) => ({
        url: '/api/feedback/thumb_survey_options',
        params
      })
    }),
    fetchTemplates: build.query({
      query: (params = {}) => ({
        url: '/api/templates',
        params
      }),
    }),
    postSetEventTemplate: build.mutation({
      query: ({ meetingUuid, templateId, isUserSelected }) => ({
        url: NOTETAKER_POST_SET_EVENT_TEMPLATE,
        method: 'POST',
        data: {
          eventUuid: meetingUuid, // ci-api uses the naming convention eventUuid over meetingUuid
          templateId,
          isUserSelected
        }
      })
    }),
    postContentEdit: build.mutation({
      query: ({ meetingUuid, targetUuid, userEditedTargetContent }) => ({
        url: NOTETAKER_POST_CONTENT_EDIT,
        method: 'POST',
        data: {
          eventUuid: meetingUuid, // ci-api uses the naming convention eventUuid over meetingUuid
          targetUuid,
          userEditedTargetContent,
          userEditedTargetContentHtml: null
        }
      }),
      onQueryStarted: async ({ targetUuid, userEditedTargetContent, meetingUuid, template }, { dispatch, queryFulfilled }) => {
        const optimisticUpdate = dispatch(
          api.util.updateQueryData('fetchTemplateSummary', { meetingUuid, template }, (draft) => {
            const target = draft.summaries.find((item) => item.targetUuid === targetUuid)
            // Update the value and edited status of the target
            if (target && 'value' in target && 'edited' in target && 'uneditedValue' in target) {
              if (userEditedTargetContent !== null) {
                if (target.uneditedValue === null) {
                  // Store the original string value for restoration
                  // Validate that target.value is an array and has valid structure
                  if (Array.isArray(target.value) && target.value.length > 0) {
                    target.uneditedValue = target.value
                      .filter((item) => item && typeof item.text === 'string')
                      .map((item) => item.text)
                      .join('')
                      .trim()
                  } else {
                    target.uneditedValue = ''
                  }
                }

                // Update the transformed array structure
                target.value = splitByLinesAndAddKeys(userEditedTargetContent)
                target.edited = true
              } else {
                // Restore from the original string value
                target.value = splitByLinesAndAddKeys(target.uneditedValue || '')
                target.edited = false
                target.uneditedValue = null
              }
            }
          })
        )

        try {
          await queryFulfilled
        } catch {
          optimisticUpdate.undo()
        }
      }
    })
  }),
  overrideExisting: true
})

export const {
  useFetchTrackedMeetingsQuery,
  useFetchTrackedMeetingWithSummaryQuery,
  useFetchTrackedMeetingWithSummariesByForeignIdQuery,
  useFetchTrackedMeetingByForeignIdQuery,
  useAddTrackedMeetingMutation,
  useAddTrackedExternalMeetingMutation,
  useDestroyTrackedMeetingMutation,
  usePostTrackedMeetingThumbsFeedbackMutation,
  useFetchTemplateSummaryQuery,
  useFetchTemplatesQuery,
  useFetchThumbFeedbackSurveyOptionsQuery,
  usePostSetEventTemplateMutation,
  usePostContentEditMutation,
  useRemoveTrackedMeetingBotMutation,
} = api
